/**
 * /accounting/ar/ready-to-bill — worklist for the controller.
 *
 * Surfaces jobs where the field has wrapped (install complete) but the office
 * hasn't sent the final invoice yet — the #1 cause of cash sitting on the
 * jobsite. Pure derivation over `jobs` + `journalEntries` for last activity.
 */
import { useMemo } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, AlertCircle } from "lucide-react";
import { CockpitPage, CockpitSection } from "@/components/cockpit/CockpitShell";
import { BooksNavRail } from "@/components/accounting/BooksNavRail";
import { Badge } from "@/components/ui/badge";
import { useAppData } from "@/data/AppDataContext";

const fmt = (n: number) => `$${n.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;

const daysSince = (iso?: string) => {
  if (!iso) return null;
  const t = Date.parse(iso);
  if (!Number.isFinite(t)) return null;
  return Math.floor((Date.now() - t) / 86_400_000);
};

export default function ReadyToBillPage() {
  const { jobs } = useAppData();

  const rows = useMemo(() => {
    return jobs
      .filter(j => {
        const isWrap = j.installStatus === "Install Complete" || j.currentStage === "Completed";
        if (!isWrap) return false;
        const cv = j.financials?.contractValue ?? 0;
        const billed = (j.financials?.invoices ?? []).reduce((s, i) => s + (i.amount || 0), 0);
        return cv - billed > 0.005;
      })
      .map(j => {
        const cv = j.financials?.contractValue ?? 0;
        const invs = j.financials?.invoices ?? [];
        const billed = invs.reduce((s, i) => s + (i.amount || 0), 0);
        const lastIssued = invs
          .map(i => i.issuedAt)
          .filter((x): x is string => !!x)
          .sort()
          .pop();
        // Best proxy for "completed at" — use last invoice or fallback to today.
        const wrappedDays = daysSince(lastIssued ?? undefined);
        return {
          jobId: j.id,
          customerName: j.customerName,
          projectType: j.projectType,
          installStatus: j.installStatus,
          contractValue: cv,
          billedToDate: billed,
          unbilled: cv - billed,
          invoiceCount: invs.length,
          wrappedDays,
        };
      })
      .sort((a, b) => b.unbilled - a.unbilled);
  }, [jobs]);

  const totalUnbilled = rows.reduce((s, r) => s + r.unbilled, 0);

  return (
    <CockpitPage>
      <BooksNavRail />
      <div className="mb-4">
        <Link to="/accounting/ar" className="text-xs text-muted-foreground hover:text-foreground inline-flex items-center gap-1 mb-2">
          <ArrowLeft className="h-3 w-3" /> AR Collections
        </Link>
        <h1 data-tour-id="ready-to-bill-table" className="text-2xl font-semibold tracking-tight">Ready to Bill</h1>
        <p className="text-sm text-muted-foreground mt-1">
          Jobs the field has finished but the office hasn't fully invoiced. Send the final invoice and turn deferred revenue into cash.
        </p>
      </div>

      <CockpitSection>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          <Tile label="Jobs awaiting final bill" value={`${rows.length}`} />
          <Tile label="Unbilled contract value" value={fmt(totalUnbilled)} tone={totalUnbilled > 0 ? "warn" : undefined} />
          <Tile label="Avg per job" value={rows.length ? fmt(totalUnbilled / rows.length) : "—"} />
        </div>
      </CockpitSection>

      <CockpitSection>
        <div className="rounded-md border border-border bg-card overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-muted/40 text-xs uppercase tracking-wide text-muted-foreground">
              <tr>
                <th className="text-left px-3 py-2">Job</th>
                <th className="text-left px-3 py-2">Customer</th>
                <th className="text-left px-3 py-2">Project</th>
                <th className="text-right px-3 py-2">Contract</th>
                <th className="text-right px-3 py-2">Billed</th>
                <th className="text-right px-3 py-2">Unbilled</th>
                <th className="text-left px-3 py-2">Stage</th>
                <th className="text-left px-3 py-2">Aged</th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 ? (
                <tr><td colSpan={8} className="p-8 text-center text-muted-foreground">
                  Nothing to bill — every wrapped job has been fully invoiced.
                </td></tr>
              ) : rows.map(r => {
                const aged = r.wrappedDays ?? 0;
                const tone = aged > 14 ? "text-rose-600" : aged > 7 ? "text-amber-600" : "text-muted-foreground";
                return (
                  <tr key={r.jobId} className="border-t border-border hover:bg-accent/30">
                    <td className="px-3 py-2 font-mono text-xs">
                      <Link to={`/accounting/reports/job-profitability/${r.jobId}`} className="hover:underline text-primary">
                        {r.jobId}
                      </Link>
                    </td>
                    <td className="px-3 py-2">{r.customerName}</td>
                    <td className="px-3 py-2 text-xs text-muted-foreground">{r.projectType ?? "—"}</td>
                    <td className="px-3 py-2 text-right tabular-nums text-muted-foreground">{fmt(r.contractValue)}</td>
                    <td className="px-3 py-2 text-right tabular-nums text-muted-foreground">{fmt(r.billedToDate)}</td>
                    <td className="px-3 py-2 text-right tabular-nums font-medium">{fmt(r.unbilled)}</td>
                    <td className="px-3 py-2">
                      <Badge variant="outline" className="text-[10px]">{r.installStatus ?? "—"}</Badge>
                    </td>
                    <td className={`px-3 py-2 text-xs ${tone}`}>
                      {r.wrappedDays == null ? "—" : `${aged}d`}
                      {aged > 14 && <AlertCircle className="h-3 w-3 inline ml-1" />}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <p className="mt-2 text-xs text-muted-foreground">
          Click a job to open its one-pager. Final invoices are created from the Job Detail page; once posted, the auto-poster releases deferred revenue and WIP automatically.
        </p>
      </CockpitSection>
    </CockpitPage>
  );
}

function Tile({ label, value, tone }: { label: string; value: string; tone?: "warn" }) {
  const toneClass = tone === "warn" ? "text-amber-600" : "";
  return (
    <div className="rounded-md border border-border bg-card p-3">
      <div className="text-xs text-muted-foreground">{label}</div>
      <div className={`text-lg font-semibold tabular-nums ${toneClass}`}>{value}</div>
    </div>
  );
}
